import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2, ShoppingCart, ArrowRight, Package, Check } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function CartPage() {
    const { user, refreshCart } = useAuth();
    const { format: fmtMoney } = useCurrency();
    const { t } = useTranslation();
    const [cart, setCart] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    const [submitError, setSubmitError] = useState("");
    const [orderNote, setOrderNote] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/cart");
            setCart(data);
        } catch {
            setCart({ items: [] });
        }
    };

    useEffect(() => {
        if (user === null) return;
        if (user === false) {
            setCart({ items: [] });
            return;
        }
        load();
    }, [user]);

    const remove = async (productId) => {
        await api.post("/cart/remove", { product_id: productId, quantity: 1 });
        await load();
        await refreshCart();
    };

    const clear = async () => {
        await api.post("/cart/clear");
        await load();
        await refreshCart();
    };

    const submitOrder = async () => {
        setSubmitting(true);
        setSubmitError("");
        setSubmitResult(null);
        try {
            const { data } = await api.post("/orders/submit", { note: orderNote || null });
            setSubmitResult(data);
            setOrderNote("");
            await load();
            await refreshCart();
        } catch (e) {
            setSubmitError(e.response?.data?.detail || "Could not submit order");
        } finally {
            setSubmitting(false);
        }
    };

    if (user === false) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="cart-login-gate">
                <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h1 className="font-display font-black text-3xl uppercase">{t("nav.login")}</h1>
                <p className="mt-3 text-slate-600">{t("auth.approved_subtitle")}</p>
                <Link to="/login" className="btn-primary mt-6 inline-flex">
                    {t("nav.login")} <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    if (!cart) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 py-12">
                <div className="skeleton h-32" />
            </div>
        );
    }

    const total = cart.items.reduce((s, it) => s + (it.price || 0) * it.quantity, 0);
    const totalQty = cart.items.reduce((s, it) => s + it.quantity, 0);

    return (
        <div className="bg-white min-h-[60vh]" data-testid="cart-page">
            <div className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-[1440px] mx-auto px-4 py-6">
                    <div className="label-kicker mb-1">{t("nav.cart")}</div>
                    <h1 className="font-display font-black text-3xl md:text-4xl uppercase">
                        {t("cart.title")} ({totalQty})
                    </h1>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
                <div>
                    {cart.items.length === 0 ? (
                        <div className="border border-slate-200 p-12 text-center">
                            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="font-mono text-slate-500 mb-4">{t("cart.empty")}</p>
                            <Link to="/catalog" className="btn-primary inline-flex">
                                {t("cart.continue_shopping")} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    ) : (
                        <div className="border border-slate-200">
                            <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 label-kicker text-slate-500 text-[10px]">
                                <div>Img</div>
                                <div>{t("product.part_number")}</div>
                                <div>{t("cart.qty")}</div>
                                <div>{t("product.price")}</div>
                                <div></div>
                            </div>
                            {cart.items.map((it) => (
                                <div
                                    key={it.product_id}
                                    className="grid grid-cols-[80px_1fr_auto_auto_auto] gap-4 px-4 py-3 border-b border-slate-100 items-center"
                                    data-testid={`cart-item-${it.product_id}`}
                                >
                                    <div className="w-20 h-20 bg-slate-50 border border-slate-200 overflow-hidden">
                                        {it.image_url ? (
                                            <img src={it.image_url} alt="" className="w-full h-full object-contain p-2" />
                                        ) : (
                                            <Package className="w-8 h-8 text-slate-300 m-auto mt-6" />
                                        )}
                                    </div>
                                    <div>
                                        <Link
                                            to={`/product/${it.product_id}`}
                                            className="font-display font-bold text-sm uppercase hover:text-[hsl(var(--primary))]"
                                        >
                                            {it.name}
                                        </Link>
                                        <div className="font-mono text-xs text-slate-500">{it.part_number}</div>
                                    </div>
                                    <div className="font-mono text-sm">x{it.quantity}</div>
                                    <div className="font-display font-bold text-sm min-w-[80px] text-right">
                                        {it.price ? fmtMoney(it.price * it.quantity) : "—"}
                                    </div>
                                    <button
                                        onClick={() => remove(it.product_id)}
                                        className="p-2 text-slate-400 hover:text-red-600"
                                        data-testid={`cart-remove-${it.product_id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            <div className="p-3 flex justify-end">
                                <button onClick={clear} className="text-xs font-mono text-slate-500 hover:text-red-600" data-testid="cart-clear">
                                    {t("cart.clear")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {cart.items.length > 0 && (
                    <aside className="border border-slate-200 p-5 h-fit sticky top-24" data-testid="cart-summary">
                        <div className="label-kicker mb-3">Summary</div>
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-600">{t("cart.items")}</span>
                            <span className="font-mono">{totalQty}</span>
                        </div>
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-600">{t("cart.subtotal")}</span>
                            <span className="font-mono">{fmtMoney(total)}</span>
                        </div>
                        <div className="flex justify-between mb-4">
                            <span className="font-display font-bold uppercase">{t("cart.total")}</span>
                            <span className="font-display font-black text-xl">{fmtMoney(total)}</span>
                        </div>
                        <div className="mb-3">
                            <label className="label-kicker mb-1 block">{t("cart.note_label")}</label>
                            <textarea
                                rows="2"
                                value={orderNote}
                                onChange={(e) => setOrderNote(e.target.value)}
                                className="input-field text-xs"
                                placeholder={t("cart.note_placeholder")}
                                data-testid="order-note-input"
                            />
                        </div>
                        {submitResult && (
                            <div className="mb-3 bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-700 font-mono" data-testid="order-submit-success">
                                ✓ #{submitResult.id.slice(0, 8)} — {t("cart.order_submitted")}
                            </div>
                        )}
                        {submitError && (
                            <div className="mb-3 bg-red-50 border border-red-200 p-2 text-xs text-red-700 font-mono" data-testid="order-submit-error">
                                {submitError}
                            </div>
                        )}
                        <button
                            onClick={submitOrder}
                            disabled={submitting || cart.items.length === 0}
                            className="btn-primary w-full justify-center disabled:opacity-40"
                            data-testid="submit-order-button"
                        >
                            {submitting ? t("cart.submitting") : <><Check className="w-4 h-4" /> {t("cart.submit_order")}</>}
                        </button>
                    </aside>
                )}
            </div>
        </div>
    );
}
